"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";

import { useModelViewerWhenVisible } from "@/lib/hooks/useModelViewerWhenVisible";
import {
  getCachedModelSrc,
  warmArModelCache,
} from "@/lib/utils/arModelCache";

const PLACEHOLDER_IMAGE = "/image/bin.png";
const MODEL_PATH = "/model/model.glb";
const MODEL_SOURCES = [MODEL_PATH];

const Model3D = () => {
  const { containerRef, ready: viewerReady } = useModelViewerWhenVisible();
  const [modelSrc, setModelSrc] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const modelRef = useRef(null);
  const cacheWarmedRef = useRef(false);

  useEffect(() => {
    if (!viewerReady) return;

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "fetch";
    link.href = MODEL_PATH;
    link.crossOrigin = "anonymous";
    document.head.appendChild(link);

    return () => {
      link.remove();
    };
  }, [viewerReady]);

  useEffect(() => {
    if (!viewerReady) return;

    let cancelled = false;

    getCachedModelSrc(MODEL_PATH)
      .then((cached) => {
        if (!cancelled) setModelSrc(cached || MODEL_PATH);
      })
      .catch(() => {
        if (!cancelled) setModelSrc(MODEL_PATH);
      });

    return () => {
      cancelled = true;
    };
  }, [viewerReady]);

  useEffect(() => {
    const model = modelRef.current;
    if (!model || !modelSrc) return;

    const handleLoad = () => {
      setIsLoaded(true);

      if (!cacheWarmedRef.current) {
        cacheWarmedRef.current = true;
        warmArModelCache(MODEL_PATH, MODEL_SOURCES).catch(() => {});
      }
    };

    model.addEventListener("load", handleLoad);
    if (model.loaded) handleLoad();

    return () => model.removeEventListener("load", handleLoad);
  }, [modelSrc, viewerReady]);

  useEffect(() => {
    const model = modelRef.current;
    if (!model || !viewerReady) return;

    const block = (e) => {
      if (e.type === "click" || e.type === "dblclick") {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const container = model.parentElement;
    if (!container) return;

    container.addEventListener("click", block, true);
    container.addEventListener("dblclick", block, true);

    return () => {
      container.removeEventListener("click", block, true);
      container.removeEventListener("dblclick", block, true);
    };
  }, [viewerReady]);

  const showImageOverlay = !viewerReady || !isLoaded;

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative flex items-center justify-center min-h-[400px]"
    >
      {viewerReady && modelSrc && (
        <model-viewer
          ref={modelRef}
          src={modelSrc}
          ios-src="/model/model.usdz"
          camera-orbit="-35deg 75deg 100%"
          field-of-view="15deg"
          min-camera-orbit="auto auto 100%"
          max-camera-orbit="auto auto 100%"
          disable-zoom
          disable-tap
          interpolation-decay="200"
          camera-controls
          auto-rotate
          rotation-per-second="30deg"
          exposure="3"
          shadow-intensity="0"
          environment-image="neutral"
          loading="eager"
          interaction-prompt="none"
          style={{
            width: "100%",
            height: "100%",
            minHeight: "400px",
            backgroundColor: "transparent",
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? "scale(1)" : "scale(0.9)",
            transition:
              "opacity 1s ease, transform 1s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
          }}
          alt="سطل زباله ی هوشمند"
        />
      )}

      {showImageOverlay && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <Image
            src={PLACEHOLDER_IMAGE}
            alt="سطل زباله ی هوشمند"
            fill
            className="object-cover h-full w-full scale-110"
            sizes="(max-width: 1024px) 100vw, 475px"
            priority
            fetchPriority="high"
          />
        </div>
      )}
    </div>
  );
};

export default Model3D;
